/* eslint-disable no-nested-ternary */
import React, { forwardRef, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { AppBar, Accordion, AccordionDetails, AccordionSummary, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { formatDateMMDDYYYY, formatDOBFromPim, formatPhoneNumber } from '@mummssoftware/utils/formatters';
import messages from './messages';
import { ActionIcon } from '../ActionIcon';
const Root = styled.div.withConfig({
  displayName: "PatientHeader__Root",
  componentId: "sc-1uy8hi1-0"
})(["flex-grow:1;@media print{display:none;}"]);
const StyledSpan = styled.span.withConfig({
  displayName: "PatientHeader__StyledSpan",
  componentId: "sc-1uy8hi1-1"
})(["margin-right:20px;font-size:14px;"]);
const IntroDiv = styled.div.withConfig({
  displayName: "PatientHeader__IntroDiv",
  componentId: "sc-1uy8hi1-2"
})(["display:flex;"]);
const withStyles = makeStyles(theme => ({
  appBar: {
    background: theme.palette.background.header,
    boxShadow: 'none'
  },
  appBarFixed: {
    marginTop: 'var(--safe-area-inset-top)'
  },
  heading: {
    fontSize: theme.typography.pxToRem(16)
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20
  },
  details: {
    alignItems: 'baseline'
  },
  column: {
    flexBasis: '33.33%'
  },
  info: {
    flexBasis: 'auto'
  },
  name: {
    flexBasis: '25%'
  },
  row: {
    flexBasis: '100%',
    alignItems: 'baseline'
  },
  helper: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2)
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  propertyTitle: {
    color: theme.palette.primary.main
  },
  responseText: {
    color: theme.palette.text.secondary
  }
}));
const StyledAccordionSummary = styled(AccordionSummary).withConfig({
  displayName: "PatientHeader__StyledAccordionSummary",
  componentId: "sc-1uy8hi1-3"
})(["&&{margin-bottom:-1px;min-height:50px;&&.Mui-expanded{min-height:50px;}}&& .MuiAccordionSummary-content.Mui-expanded{margin:12px 0;}"]);

var _ref = /*#__PURE__*/React.createElement("br", null);

var _ref2 = /*#__PURE__*/React.createElement(ExpandMoreIcon, null);

var _ref3 = /*#__PURE__*/React.createElement("br", null);

var _ref4 = /*#__PURE__*/React.createElement("br", null);

var _ref5 = /*#__PURE__*/React.createElement("br", null);

var _ref6 = /*#__PURE__*/React.createElement("br", null);

var _ref7 = /*#__PURE__*/React.createElement("br", null);

var _ref8 = /*#__PURE__*/React.createElement("br", null);

var _ref9 = /*#__PURE__*/React.createElement("br", null);

var _ref10 = /*#__PURE__*/React.createElement("br", null);

var _ref11 = /*#__PURE__*/React.createElement("br", null);

var _ref12 = /*#__PURE__*/React.createElement("br", null);

var _ref13 = /*#__PURE__*/React.createElement("br", null);

var _ref14 = /*#__PURE__*/React.createElement("br", null);

var _ref15 = /*#__PURE__*/React.createElement("br", null);

export const PatientHeader = /*#__PURE__*/forwardRef((props, ref) => {
  const classes = withStyles();
  const {
    formatMessage
  } = useIntl();
  const appBarPosition = 'fixed';
  const noData = formatMessage(messages.unknown);
  const {
    palette: {
      text: {
        secondary
      }
    }
  } = useTheme();
  /** Confirm a patient was passed to the header */

  if ('patient' in props && typeof props.patient !== 'undefined') {
    const {
      /** Extract the patient details */
      patient: {
        patientNumber,
        firstName,
        lastName,
        dateOfBirth,
        age,
        gender,
        sex,
        diagnoses,
        primaryPhone,
        primaryAddress,
        ssn,
        programSiteName,
        maritalStatus,
        race,
        ethnicity,
        dischargeDate,
        dischargeReason,
        referralDate,
        caregiverFullName,
        caregiverPhone,
        caregiverRelation,
        admissionDate,
        insuranceCoverageDTOList,
        patientPhysicianDTOList,
        loc,
        siteName,
        status,
        county
      }
    } = props;
    const {
      street1,
      street2,
      city,
      state,
      zip
    } = primaryAddress;
    const patientPhyInfo = (patientPhysicianDTOList === null || patientPhysicianDTOList === void 0 ? void 0 : patientPhysicianDTOList[0]) || {};
    let title;

    if (insuranceCoverageDTOList && insuranceCoverageDTOList.length > 1) {
      const newList = insuranceCoverageDTOList.slice(1);
      title = newList.reduce((seed, coverage) => {
        const {
          carrierName,
          policyNumber
        } = coverage;
        seed.push(`${carrierName} - ${policyNumber}`);
        return seed;
      }, []);
    }

    const tooltipTitle = () => /*#__PURE__*/React.createElement(Fragment, null, title && title.map(text => /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption"
    }, text), _ref)));

    return /*#__PURE__*/React.createElement(Root, null, /*#__PURE__*/React.createElement(AppBar, {
      ref: ref,
      "data-testid": "MUI-appBar",
      className: classes.appBar,
      classes: {
        positionFixed: classes.appBarFixed
      },
      position: appBarPosition,
      color: "secondary",
      id: "topbar"
    }, /*#__PURE__*/React.createElement(Accordion, {
      square: true
    }, /*#__PURE__*/React.createElement(StyledAccordionSummary, {
      expandIcon: _ref2,
      "aria-controls": "panel1c-content",
      id: "panel1c-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.name
    }, /*#__PURE__*/React.createElement(Typography, null, /*#__PURE__*/React.createElement("span", {
      className: classes.heading
    }, `${firstName} ${lastName} (${patientNumber})`))), /*#__PURE__*/React.createElement(IntroDiv, {
      className: classes.row
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.info
    }, /*#__PURE__*/React.createElement(Typography, null, dateOfBirth ? /*#__PURE__*/React.createElement(StyledSpan, null, "DOB:", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, formatDOBFromPim(dateOfBirth), " (", age, ")")) : null)), /*#__PURE__*/React.createElement("div", {
      className: classes.info
    }, /*#__PURE__*/React.createElement(Typography, null, /*#__PURE__*/React.createElement(StyledSpan, null, formatMessage(messages.gender), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, (gender === null || gender === void 0 ? void 0 : gender.charAt(0)) || (sex === null || sex === void 0 ? void 0 : sex.charAt(0)))))), /*#__PURE__*/React.createElement("div", {
      className: classes.info
    }, /*#__PURE__*/React.createElement(Typography, null, /*#__PURE__*/React.createElement(StyledSpan, null, "Status:", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, status)))), /*#__PURE__*/React.createElement("div", {
      className: classes.info
    }, /*#__PURE__*/React.createElement(Typography, null, /*#__PURE__*/React.createElement(StyledSpan, null, formatMessage(messages.diagnosis), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, Array.isArray(diagnoses) && diagnoses.length ? diagnoses[0] : diagnoses || noData)))))), /*#__PURE__*/React.createElement(AccordionDetails, {
      className: classes.details
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.column
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption"
    }, referralDate ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage(messages.referred), ' ', formatMessage(messages.to), ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, programSiteName, " ", formatMessage(messages.asof), ' ', formatDateMMDDYYYY(referralDate)), _ref3) : null, admissionDate ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage(messages.admitted), ' ', formatMessage(messages.to), ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, programSiteName, " ", formatMessage(messages.asof), ' ', formatDateMMDDYYYY(admissionDate)), _ref4) : null, dischargeDate ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage(messages.discharged), ' ', formatMessage(messages.from), ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, programSiteName, " ", formatMessage(messages.asof), ' ', formatDateMMDDYYYY(dischargeDate)), _ref5) : null, dischargeReason ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage(messages.reason), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, dischargeReason), _ref6) : null, formatMessage(messages.site), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, siteName), _ref7)), /*#__PURE__*/React.createElement("div", {
      className: classes.column
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption"
    }, "SSN: ", /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, ssn), _ref8, formatMessage(messages.phone), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, primaryPhone ? formatPhoneNumber(primaryPhone) : noData), _ref9, "CG:", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, caregiverFullName ? `${caregiverFullName} ${caregiverRelation} ${formatPhoneNumber(caregiverPhone || '')}` : noData), _ref10, "Phys:", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, patientPhysicianDTOList !== null && patientPhysicianDTOList !== void 0 && patientPhysicianDTOList.length ? `Dr. ${patientPhysicianDTOList[0].physicianName} ${patientPhyInfo.credentials || ''} ${patientPhyInfo.primaryNumber ? formatPhoneNumber(patientPhyInfo.primaryNumber) : ''} ` : formatMessage(messages.noPhysician)), _ref11, formatMessage(messages.insurance), ":", ' ', insuranceCoverageDTOList && insuranceCoverageDTOList.length > 1 ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, insuranceCoverageDTOList[0].carrierName, " -", insuranceCoverageDTOList[0].policyNumber), /*#__PURE__*/React.createElement(ActionIcon, {
      title: tooltipTitle(),
      style: {
        paddingBottom: 6,
        margin: 0
      },
      htmlColor: secondary,
      Icon: InfoIcon
    })) : insuranceCoverageDTOList && insuranceCoverageDTOList.length === 1 ? /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, insuranceCoverageDTOList[0].carrierName, " -", insuranceCoverageDTOList[0].policyNumber) : /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, noData))), /*#__PURE__*/React.createElement("div", {
      className: classes.column
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption"
    }, formatMessage(messages.street), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, street1, " ", street2), _ref12, formatMessage(messages.city), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, city, ", ", state, ", ", zip), _ref13, formatMessage(messages.county), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, county || noData), _ref14, formatMessage(messages.location), ":", ' ', /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, loc), _ref15, /*#__PURE__*/React.createElement("span", {
      className: classes.responseText
    }, `${maritalStatus || `Marital Status`} / ${race || `race`} / ${ethnicity || `Ethnicity`} / ${(gender === null || gender === void 0 ? void 0 : gender.charAt(0)) || (sex === null || sex === void 0 ? void 0 : sex.charAt(0)) || `Gender`}`)))))));
  }
  /** Else no patient has been passed to the header */


  return null;
});
export default PatientHeader;